import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Descriptions, 
  Image, 
  Tag, 
  Spin, 
  Result, 
  Button, 
  Space,
  Input,
  Form,
  Modal
} from 'antd';
import { 
  LockOutlined, 
  HomeOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { getShareableLink } from '../services/shipmentService';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import { fixImageUrl } from '../utils/imageUtils';
import { getConfig, getImageServerUrl } from '../services/api';

const { Title, Text, Paragraph } = Typography;

const Share = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shipment, setShipment] = useState(null);
  const [error, setError] = useState(null);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [form] = Form.useForm();
  
  // 获取图片服务器URL
  const imageServerUrl = getImageServerUrl();
  
  // 加载共享运单信息
  const loadShipment = async (password = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await getShareableLink(id, password);
      
      if (result.success) {
        setShipment(result.shipment);
      } else {
        if (result.passwordRequired) {
          setPasswordModalVisible(true);
        } else {
          setError(result.message || '获取运单信息失败');
        }
      }
    } catch (error) {
      setError(error.message || '获取运单信息失败');
    } finally {
      setLoading(false);
    }
  };
  
  // 组件加载时获取运单信息
  useEffect(() => {
    if (id) {
      loadShipment();
    }
  }, [id]);
  
  // 处理密码提交
  const handlePasswordSubmit = async () => {
    try {
      const values = await form.validateFields();
      setPasswordModalVisible(false);
      loadShipment(values.password);
    } catch (error) {
      // 表单验证失败
    }
  };
  
  // 处理打印
  const handlePrint = () => {
    window.print();
  };
  
  // 返回首页
  const goHome = () => {
    navigate('/');
  };
  
  // 如果正在加载
  if (loading && !error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', flexDirection: 'column' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>加载中...</div>
      </div>
    );
  }
  
  // 如果有错误
  if (error) {
    return (
      <Result
        status="error"
        title="获取运单信息失败"
        subTitle={error}
        extra={[
          <Button type="primary" key="home" onClick={goHome}>
            返回首页
          </Button>
        ]}
      />
    );
  }
  
  // 如果没有运单信息
  if (!shipment) {
    return (
      <Result
        status="404"
        title="未找到运单信息"
        subTitle="您访问的运单不存在或链接已过期"
        extra={[
          <Button type="primary" key="home" onClick={goHome}>
            返回首页
          </Button>
        ]}
      />
    );
  }
  
  return (
    <div className="share-page">
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={4}>运单详情</Title>
            <Button 
              type="primary" 
              icon={<PrinterOutlined />}
              onClick={handlePrint}
            >
              打印
            </Button>
          </div>
        }
        className="print-friendly"
      >
        <div className="print-header" style={{ display: 'none', textAlign: 'center', marginBottom: 20 }}>
          <Title level={3}>运单详情</Title>
          <Paragraph>打印时间: {formatDateTime(Date.now())}</Paragraph>
        </div>
        
        <Descriptions bordered column={1}>
          <Descriptions.Item label="快递单号">{shipment.tracking_no || shipment.tracking}</Descriptions.Item>
          <Descriptions.Item label="客户姓名">{shipment.name}</Descriptions.Item>
          <Descriptions.Item label="联系电话">{shipment.phone}</Descriptions.Item>
          <Descriptions.Item label="地址">{shipment.address}</Descriptions.Item>
          <Descriptions.Item label="货物内容">{shipment.content}</Descriptions.Item>
          <Descriptions.Item label="日期">{formatDate(shipment.date)}</Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={
              shipment.status === '已入库' ? 'blue' : 
              shipment.status === '已出库' ? 'green' : 
              shipment.status === '运输中' ? 'orange' : 
              shipment.status === '已签收' ? 'green' : 'default'
            }>
              {shipment.status}
            </Tag>
          </Descriptions.Item>
          {shipment.weight && (
            <Descriptions.Item label="重量">{shipment.weight} kg</Descriptions.Item>
          )}
          {shipment.quantity && (
            <Descriptions.Item label="数量">{shipment.quantity}</Descriptions.Item>
          )}
          {shipment.notes && (
            <Descriptions.Item label="备注">{shipment.notes}</Descriptions.Item>
          )}
        </Descriptions>
        
        {/* 处理图片展示 - 兼容两种格式 */}
        {shipment.images && shipment.images.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <Title level={5}>货物图片</Title>
            <Image.PreviewGroup>
              <Space wrap>
                {shipment.images.map((image, index) => (
                  <Image
                    key={index}
                    width={120}
                    src={fixImageUrl(image, imageServerUrl)}
                    alt={`货物图片 ${index + 1}`}
                  />
                ))}
              </Space>
            </Image.PreviewGroup>
          </div>
        )}
        
        {/* 处理新格式的图片展示 - image_urls 包含 cargo/packing/shipping 类别 */}
        {shipment.image_urls && (
          <>
            {shipment.image_urls.cargo && shipment.image_urls.cargo.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <Title level={5}>货物图片</Title>
                <Image.PreviewGroup>
                  <Space wrap>
                    {shipment.image_urls.cargo.map((image, index) => (
                      <Image
                        key={`cargo-${index}`}
                        width={120}
                        src={fixImageUrl(image, imageServerUrl)}
                        alt={`货物图片 ${index + 1}`}
                      />
                    ))}
                  </Space>
                </Image.PreviewGroup>
              </div>
            )}
            
            {shipment.image_urls.packing && shipment.image_urls.packing.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <Title level={5}>包装图片</Title>
                <Image.PreviewGroup>
                  <Space wrap>
                    {shipment.image_urls.packing.map((image, index) => (
                      <Image
                        key={`packing-${index}`}
                        width={120}
                        src={fixImageUrl(image, imageServerUrl)}
                        alt={`包装图片 ${index + 1}`}
                      />
                    ))}
                  </Space>
                </Image.PreviewGroup>
              </div>
            )}
            
            {shipment.image_urls.shipping && shipment.image_urls.shipping.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <Title level={5}>发货图片</Title>
                <Image.PreviewGroup>
                  <Space wrap>
                    {shipment.image_urls.shipping.map((image, index) => (
                      <Image
                        key={`shipping-${index}`}
                        width={120}
                        src={fixImageUrl(image, imageServerUrl)}
                        alt={`发货图片 ${index + 1}`}
                      />
                    ))}
                  </Space>
                </Image.PreviewGroup>
              </div>
            )}
          </>
        )}
        
        <div style={{ marginTop: 20 }}>
          <Text type="secondary">
            此信息由仓库管理系统提供，如有疑问请联系管理员。
          </Text>
        </div>
      </Card>
      
      {/* 密码输入模态框 */}
      <Modal
        title="需要密码"
        open={passwordModalVisible}
        onOk={handlePasswordSubmit}
        onCancel={() => navigate('/')}
        okText="确认"
        cancelText="取消"
        closable={false}
        maskClosable={false}
      >
        <Form form={form}>
          <Paragraph>
            此运单信息受密码保护，请输入密码查看：
          </Paragraph>
          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="请输入密码" 
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 添加打印样式 */}
      <style jsx="true">{`
        @media print {
          .ant-layout-header,
          .ant-layout-sider,
          .ant-card-extra,
          .ant-card-head-title button,
          .ant-btn {
            display: none !important;
          }
          
          .ant-layout-content {
            margin: 0 !important;
            padding: 0 !important;
          }
          
          .ant-card {
            border: none !important;
            box-shadow: none !important;
          }
          
          .print-header {
            display: block !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Share;
